import React, { useState, useEffect } from 'react';
import { 
  getBlockedDates, 
  getAllRooms, 
  createBlockedDate, 
  updateBlockedDate, 
  deleteBlockedDate 
} from '../../api';

const BlockedDatesManagement = () => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlockedDate, setEditingBlockedDate] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, expired
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    room_ids: []
  });

  useEffect(() => {
    fetchBlockedDates();
    fetchRooms();
  }, []);

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const fetchBlockedDates = async () => {
    try {
      const data = await getBlockedDates();
      setBlockedDates(data);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
      showNotification('Failed to fetch blocked dates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const data = await getAllRooms();
      setRooms(data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleRoom = (roomId) => {
    setFormData(prev => ({
      ...prev,
      room_ids: prev.room_ids.includes(roomId)
        ? prev.room_ids.filter(id => id !== roomId)
        : [...prev.room_ids, roomId]
    }));
  };

  const toggleAllRooms = () => {
    const allRoomIds = rooms.map(room => room.id);
    setFormData(prev => ({
      ...prev,
      room_ids: prev.room_ids.length === allRoomIds.length ? [] : allRoomIds
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!formData.start_date || !formData.end_date) {
        throw new Error('Start date and end date are required');
      }

      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        throw new Error('Start date cannot be after end date');
      }

      if (formData.room_ids.length === 0) {
        throw new Error('Please select at least one room to block');
      }

      if (editingBlockedDate) {
        await updateBlockedDate(editingBlockedDate.id, formData);
        showNotification('Blocked date updated successfully');
      } else {
        await createBlockedDate(formData);
        showNotification('Blocked date created successfully');
      }

      resetForm();
      fetchBlockedDates();
    } catch (error) {
      showNotification(error.response?.data?.error || error.message || 'Failed to save blocked date', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    try {
      // Handle MySQL date format (YYYY-MM-DD) or datetime format
      if (dateString.includes(' ')) {
        // If it has time component, just take the date part
        return dateString.split(' ')[0];
      }
      
      // For other formats, try to parse and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return '';
      }
      
      // Use getFullYear, getMonth, getDate to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  const handleEdit = (blockedDate) => {
    console.log('Editing blocked date:', blockedDate);
    setEditingBlockedDate(blockedDate);
    setFormData({
      start_date: formatDateForInput(blockedDate.start_date),
      end_date: formatDateForInput(blockedDate.end_date),
      reason: blockedDate.reason || '',
      room_ids: blockedDate.rooms ? blockedDate.rooms.map(room => room.id) : []
    });
    console.log('Form data set to:', {
      start_date: formatDateForInput(blockedDate.start_date),
      end_date: formatDateForInput(blockedDate.end_date),
      reason: blockedDate.reason || '',
      room_ids: blockedDate.rooms ? blockedDate.rooms.map(room => room.id) : []
    });
    setShowForm(true);
  };

  const handleDelete = (blockedDate) => {
    setItemToDelete(blockedDate);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteBlockedDate(itemToDelete.id);
      fetchBlockedDates();
      showNotification('Blocked date deleted successfully!', 'success');
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting blocked date:', error);
      showNotification('Error deleting blocked date. Please try again.', 'error');
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      start_date: '',
      end_date: '',
      reason: '',
      room_ids: []
    });
    setEditingBlockedDate(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const isActive = (startDate, endDate) => {
    const now = new Date();
    return new Date(startDate) <= now && new Date(endDate) >= now;
  };

  const getStatus = (startDate, endDate) => {
    if (isExpired(endDate)) return 'expired';
    if (isActive(startDate, endDate)) return 'active';
    return 'upcoming';
  };

  const getStatusBadge = (startDate, endDate) => {
    const status = getStatus(startDate, endDate);
    const badges = {
      active: { text: 'Active', className: 'status-active' },
      upcoming: { text: 'Upcoming', className: 'status-upcoming' },
      expired: { text: 'Expired', className: 'status-expired' }
    };
    const badge = badges[status];
    return <span className={`status-badge ${badge.className}`}>{badge.text}</span>;
  };

  const filteredBlockedDates = blockedDates.filter(blockedDate => {
    if (filter === 'all') return true;
    if (filter === 'active') return isActive(blockedDate.start_date, blockedDate.end_date);
    if (filter === 'expired') return isExpired(blockedDate.end_date);
    if (filter === 'upcoming') return !isActive(blockedDate.start_date, blockedDate.end_date) && !isExpired(blockedDate.end_date);
    return true;
  });

  if (loading) {
    return <div className="loading">Loading blocked dates...</div>;
  }

  return (
    <div className="blocked-dates-management">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="blocked-dates-management-header">
        <h2>Blocked Dates Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add New Blocked Date
        </button>
      </div>

      {/* Filter Controls */}
      <div className="filter-controls">
        <label>Filter by status:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="upcoming">Upcoming</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Blocked Dates Table */}
      <div className="rooms-table">
        <table>
          <thead>
            <tr>
              <th>Date Range</th>
              <th>Duration</th>
              <th>Reason</th>
              <th>Blocked Rooms</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBlockedDates.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {filter === 'all' ? 'No blocked dates found' : `No ${filter} blocked dates found`}
                </td>
              </tr>
            ) : (
              filteredBlockedDates.map((blockedDate) => (
                <tr key={blockedDate.id}>
                  <td>
                    <div className="date-range">
                      <div>{formatDate(blockedDate.start_date)}</div>
                      <div className="to">to</div>
                      <div>{formatDate(blockedDate.end_date)}</div>
                    </div>
                  </td>
                  <td>
                    {Math.ceil((new Date(blockedDate.end_date) - new Date(blockedDate.start_date)) / (1000 * 60 * 60 * 24)) + 1} days
                  </td>
                  <td>{blockedDate.reason || 'No reason specified'}</td>
                  <td>
                    <div className="blocked-rooms">
                      {blockedDate.rooms && blockedDate.rooms.length > 0 ? (
                        blockedDate.rooms.length <= 3 ? (
                          blockedDate.rooms.map(room => room.name).join(', ')
                        ) : (
                          `${blockedDate.rooms.slice(0, 3).map(room => room.name).join(', ')} +${blockedDate.rooms.length - 3} more`
                        )
                      ) : (
                        'No rooms specified'
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(blockedDate.start_date, blockedDate.end_date)}</td>
                  <td>{formatDate(blockedDate.created_at)}</td>
                  <td>
                    <button 
                      className="btn btn-small btn-secondary"
                      onClick={() => handleEdit(blockedDate)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(blockedDate)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingBlockedDate ? 'Edit Blocked Date' : 'Add New Blocked Date'}</h3>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="start_date">Start Date *</label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="end_date">End Date *</label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason (Optional)</label>
                <input
                  type="text"
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="e.g., Maintenance, Holiday, Private Event"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Rooms to Block *</label>
                <div className="rooms-selection">
                  <div className="select-all-container">
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={toggleAllRooms}
                      disabled={loading}
                    >
                      {formData.room_ids.length === rooms.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="selection-count">
                      {formData.room_ids.length} of {rooms.length} rooms selected
                    </span>
                  </div>

                  <div className="rooms-grid">
                    {rooms.map((room) => (
                      <div
                        key={room.id}
                        className={`room-option ${formData.room_ids.includes(room.id) ? 'selected' : ''}`}
                        onClick={() => !loading && toggleRoom(room.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          margin: '4px 0',
                          cursor: 'pointer',
                          backgroundColor: formData.room_ids.includes(room.id) ? '#e3f2fd' : '#fff'
                        }}
                      >
                        <div className="room-info" style={{ flex: 1 }}>
                          <div className="room-name" style={{ fontWeight: '500', marginBottom: '2px' }}>
                            {room.room_number || room.room_type_name}
                          </div>
                          <div className="room-type" style={{ fontSize: '0.85em', color: '#666' }}>
                            {room.room_type_name}
                          </div>
                        </div>
                        <div className="room-checkbox" style={{ marginLeft: '12px' }}>
                          <input
                            type="checkbox"
                            checked={formData.room_ids.includes(room.id)}
                            onChange={() => toggleRoom(room.id)}
                            disabled={loading}
                            style={{ margin: 0 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingBlockedDate ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete the blocked date from <strong>{formatDate(itemToDelete?.start_date)} to {formatDate(itemToDelete?.end_date)}</strong>?</p>
            <p>This action cannot be undone.</p>
            <div className="form-actions">
              <button 
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Delete Blocked Date
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockedDatesManagement;